import { useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { fileService } from '../services/fileService';
import { useFileStore } from '../store/fileStore';
import { useTaxYearStore } from '@/features/tax-year/store/taxYearStore';
import { handleApiError } from '@/lib/utils/errorHandler';

export const useDocuments = () => {
  const {
    documents,
    isLoading,
    error,
    totalDocuments,
    currentPage,
    setDocuments,
    removeDocument,
    setLoading,
    setError,
    setPage,
  } = useFileStore();
  const effectiveYear = useTaxYearStore((s) => s.getEffectiveYear());
  const configFetched = useTaxYearStore((s) => s.configFetched);

  const fetchDocuments = useCallback(
    async (page = 1, _limit = 10) => {
      if (effectiveYear == null) return;
      setLoading(true);
      try {
        const response = await fileService.getDocuments(effectiveYear);
        setDocuments(response.documents, response.total || response.documents.length);
        setPage(page);
      } catch (err) {
        const apiError = handleApiError(err);
        setError(apiError.message);
      }
    },
    [effectiveYear, setDocuments, setLoading, setError, setPage]
  );

  const deleteDocument = useCallback(
    async (documentId: string): Promise<boolean> => {
      try {
        await fileService.deleteDocument(documentId);
        removeDocument(documentId);
        toast.success('Document deleted successfully');
        return true;
      } catch (err) {
        handleApiError(err);
        return false;
      }
    },
    [removeDocument]
  );

  const getPreviewUrl = useCallback(async (documentId: string): Promise<string | null> => {
    try {
      const response = await fileService.getPreviewUrl(documentId);
      return response.url;
    } catch (err) {
      handleApiError(err);
      return null;
    }
  }, []);

  const refreshDocuments = useCallback(() => {
    fetchDocuments(currentPage);
  }, [fetchDocuments, currentPage]);

  // Fetch documents once tax year is ready and when selected year changes
  useEffect(() => {
    if (!configFetched || effectiveYear == null) return;
    fetchDocuments();
  }, [configFetched, effectiveYear, fetchDocuments]);

  return {
    documents,
    isLoading: isLoading || !configFetched,
    error,
    totalDocuments,
    currentPage,
    fetchDocuments,
    deleteDocument,
    getPreviewUrl,
    refreshDocuments,
  };
};
